// WeeklyWrapCard.js
// Renders a single WeeklyWrapData document from Firestore.
// Uses accordion sections to keep the card manageable.
import React, { useState } from 'react';
import MMIGauge from './MMIGauge';
import SortableSectorList from './SortableSectorList';
import './MarketWrap.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

const ArrowUp   = () => <span className="arrow-up">▲</span>;
const ArrowDown = () => <span className="arrow-down">▼</span>;

// direction prop: 'up' | 'down' | 'auto' (default)
// Pass direction={row.direction} when wowPercent is stored as an absolute
// positive value with a separate direction field (movers, sectors, etc.).
const ChangeCell = ({ value, direction = 'auto' }) => {
    const v = Number(value ?? 0);
    const isUp = direction === 'up' ? true : direction === 'down' ? false : v >= 0;
    const cls = isUp ? 'chg-up' : 'chg-down';
    return (
        <span className={cls}>
            {isUp ? <ArrowUp /> : <ArrowDown />}
            {Math.abs(v).toFixed(2)}%
        </span>
    );
};

const formatClose = (v) =>
    v ? Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—';

const toneCls = (tone) => {
    const map = { Positive: 'tone-positive', Cautious: 'tone-cautious', Volatile: 'tone-volatile', Mixed: 'tone-mixed' };
    return map[tone] || '';
};

// Section block with optional collapse.
// The toggle is a real <button> inside the heading so it is keyboard-operable
// and exposes expanded/collapsed state to assistive tech (audit W1, G5).
const Section = ({ title, children, defaultOpen = true }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="wrap-section">
            <h3 className="wrap-section-title">
                <button
                    type="button"
                    className="wrap-section-toggle"
                    aria-expanded={open}
                    onClick={() => setOpen(!open)}
                >
                    {title} <span aria-hidden="true" style={{ float: 'right', fontSize: '0.7rem' }}>{open ? '▲' : '▼'}</span>
                </button>
            </h3>
            {open && children}
        </div>
    );
};

// ── Main component ────────────────────────────────────────────────────────────

const WeeklyWrapCard = ({ wrap }) => {
    if (!wrap || !wrap.data) return null;

    const { data, title, createdAt } = wrap;
    const {
        meta, benchmarks, institutionalFlows, broaderMarket,
        sectoralPerformance,
        stocksInNews, globalIndices, currency, commodities,
        optionsData, ipoTracker, resultsCalendar, lookingAhead, ourView,
        mmi,
    } = data;

    // weekEndDate is already a human-readable string e.g. "6 March 2026" — use directly.
    // Fall back to createdAt only if weekEndDate is missing.
    const dateLabel = meta?.weekEndDate ||
        (createdAt
            ? new Date(createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
            : '');

    return (
        <div className="wrap-card">
            {/* Header */}
            <div className="wrap-card-header">
                <div>
                    <h2 className="wrap-card-title">{title || `Weekly Market Wrap — ${meta?.weekEndDate}`}</h2>
                    <p className="wrap-card-date">Week ending {dateLabel}</p>
                </div>
                <span className={`wrap-tone-badge ${toneCls(meta?.openingTone)}`}>
                    {meta?.openingTone || 'Mixed'}
                </span>
            </div>

            <div className="wrap-card-body">

                {/* MMI Gauge */}
                <MMIGauge mmi={mmi} type="weekly" />

                {/* Opening */}
                {meta?.openingSummary && (
                    <div className="wrap-opening">
                        <strong>Key Theme:</strong> {meta.keyTheme && <em>{meta.keyTheme} — </em>}
                        {meta.openingSummary}
                    </div>
                )}

                {/* Benchmarks */}
                {benchmarks?.rows?.length > 0 && (
                    <Section title="Indian Benchmarks" defaultOpen>
                        <table className="benchmark-table">
                            <caption className="visually-hidden">Benchmark index closes and week-on-week change</caption>
                            <thead>
                                <tr>
                                    <th scope="col">Index</th>
                                    <th scope="col">Close</th>
                                    <th scope="col">WoW Chg</th>
                                </tr>
                            </thead>
                            <tbody>
                                {benchmarks.rows.map((row, i) => (
                                    <tr key={i}>
                                        <td>{row.name}</td>
                                        <td>{formatClose(row.close)}</td>
                                        <td><ChangeCell value={row.wowPercent} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {benchmarks.narrative && (
                            <p style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '0.5rem' }}>
                                {benchmarks.narrative}
                            </p>
                        )}
                    </Section>
                )}

                {/* Top Weekly Movers — hidden until WoW% calculation is verified accurate */}

                {/* Sectoral Performance */}
                {sectoralPerformance?.rows?.length > 0 && (
                    <Section title="Sectoral Performance">
                        <SortableSectorList rows={sectoralPerformance.rows} />
                    </Section>
                )}

                {/* Stocks in News — positive group first, then negative */}
                {stocksInNews?.length > 0 && (() => {
                    const positives = stocksInNews.filter(i => i.sentiment === 'positive');
                    const negatives = stocksInNews.filter(i => i.sentiment === 'negative');
                    const renderItem = (item, i) => (
                        <div key={i} className="news-item">
                            <div className={`news-sentiment-dot ${item.sentiment}`} />
                            <span>
                                <span className="news-company">{item.company}:</span>
                                <span className="news-summary"> {item.summary}</span>
                            </span>
                        </div>
                    );
                    return (
                        <Section title="Stocks in the News">
                            {positives.length > 0 && (
                                <>
                                    <p style={{ fontSize: '0.72rem', color: '#15803d', fontWeight: 700, marginBottom: '0.35rem', letterSpacing: '0.05em' }}>POSITIVE</p>
                                    {positives.map(renderItem)}
                                </>
                            )}
                            {negatives.length > 0 && (
                                <>
                                    <p style={{ fontSize: '0.72rem', color: '#b91c1c', fontWeight: 700, margin: `${positives.length > 0 ? '0.75rem' : '0'} 0 0.35rem`, letterSpacing: '0.05em' }}>NEGATIVE</p>
                                    {negatives.map(renderItem)}
                                </>
                            )}
                        </Section>
                    );
                })()}

                {/* Institutional Flows — hidden when both net values are 0 (data unavailable) */}
                {institutionalFlows && (institutionalFlows.fiiNet !== 0 || institutionalFlows.diiNet !== 0) && (
                    <Section title="Institutional Flows" defaultOpen={false}>
                                <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                                    {institutionalFlows.fiiNet !== undefined && (
                                        <p style={{ margin: '0.25rem 0' }}>
                                            FII Weekly Net: <strong className={institutionalFlows.fiiNet >= 0 ? 'chg-up' : 'chg-down'}>
                                                ₹{Math.abs(institutionalFlows.fiiNet).toLocaleString('en-IN')}Cr — {institutionalFlows.fiiDirection}
                                            </strong>
                                            {institutionalFlows.fiiNote && ` · ${institutionalFlows.fiiNote}`}
                                        </p>
                                    )}
                                    {institutionalFlows.diiNet !== undefined && (
                                        <p style={{ margin: '0.25rem 0' }}>
                                            DII Weekly Net: <strong className={institutionalFlows.diiNet >= 0 ? 'chg-up' : 'chg-down'}>
                                                ₹{Math.abs(institutionalFlows.diiNet).toLocaleString('en-IN')}Cr — {institutionalFlows.diiDirection}
                                            </strong>
                                            {institutionalFlows.diiNote && ` · ${institutionalFlows.diiNote}`}
                                        </p>
                                    )}
                                    {institutionalFlows.narrative && (
                                        <p style={{ margin: '0.5rem 0 0', color: '#6b7280', fontSize: '0.82rem' }}>
                                            {institutionalFlows.narrative}
                                        </p>
                                    )}
                                </div>
                            </Section>
                        )}

                        {/* Global Indices */}
                        {globalIndices?.rows?.length > 0 && (() => {
                            const rows = globalIndices.rows.filter(r => Number(r.wowPercent) !== 0);
                            return rows.length > 0 ? (
                                <Section title="Global Indices" defaultOpen={false}>
                                    <table className="benchmark-table">
                                        <caption className="visually-hidden">Global indices week-on-week change</caption>
                                        <thead>
                                            <tr><th scope="col">Index</th><th scope="col">WoW Chg</th></tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((row, i) => (
                                                <tr key={i}>
                                                    <td>{row.index}</td>
                                                    <td><ChangeCell value={row.wowPercent} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {globalIndices.narrative && (
                                        <p style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '0.5rem' }}>
                                            {globalIndices.narrative}
                                        </p>
                                    )}
                                </Section>
                            ) : null;
                        })()}

                        {/* Commodities */}
                        {commodities?.rows?.length > 0 && (() => {
                            const rows = commodities.rows.filter(r => Number(r.wowPercent) !== 0);
                            return rows.length > 0 ? (
                                <Section title="Commodities" defaultOpen={false}>
                                    <table className="benchmark-table">
                                        <caption className="visually-hidden">Commodity prices week-on-week change</caption>
                                        <thead>
                                            <tr><th scope="col">Commodity</th><th scope="col">Unit</th><th scope="col">WoW Chg</th></tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((row, i) => (
                                                <tr key={i}>
                                                    <td>{row.name}</td>
                                                    <td style={{ color: '#6b7280' }}>{row.unit}</td>
                                                    <td><ChangeCell value={row.wowPercent} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Section>
                            ) : null;
                        })()}

                        {/* Currency */}
                        {currency?.rows?.length > 0 && (() => {
                            const rows = currency.rows.filter(r => Number(r.wowPercent) !== 0);
                            return rows.length > 0 ? (
                                <Section title="Currency" defaultOpen={false}>
                                    {rows.map((row, i) => (
                                        <div key={i} className="sector-row">
                                            <span className="sector-name">{row.pair}</span>
                                            <ChangeCell value={row.wowPercent} />
                                        </div>
                                    ))}
                                </Section>
                            ) : null;
                        })()}

                        {/* Broader Market */}
                        {broaderMarket && (
                            <Section title="Broader Market" defaultOpen={false}>
                                <table className="benchmark-table">
                                    <caption className="visually-hidden">Broader market week-on-week change</caption>
                                    <tbody>
                                        <tr>
                                            <th scope="row">Large Cap</th>
                                            <td><ChangeCell value={broaderMarket.largeCapWoW} /></td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Mid Cap</th>
                                            <td><ChangeCell value={broaderMarket.midCapWoW} /></td>
                                        </tr>
                                        <tr>
                                            <th scope="row">Small Cap</th>
                                            <td><ChangeCell value={broaderMarket.smallCapWoW} /></td>
                                        </tr>
                                        {(broaderMarket.advances !== 0 || broaderMarket.declines !== 0) && (
                                            <tr>
                                                <th scope="row">Advances / Declines ({broaderMarket.advanceDeclineDate})</th>
                                                <td>
                                                    <span className="chg-up">{broaderMarket.advances}▲</span>
                                                    {' / '}
                                                    <span className="chg-down">{broaderMarket.declines}▼</span>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </Section>
                        )}

                        {/* Options Data — hidden when all key values are absent */}
                        {optionsData && !!(optionsData.niftyPCR || optionsData.bankNiftyPCR ||
                            optionsData.niftySupport || optionsData.niftyResistance) && (
                            <Section title="Options Data" defaultOpen={false}>
                                <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                                    <p style={{ margin: '0.2rem 0' }}>
                                        Nifty PCR: <strong>{optionsData.niftyPCR}</strong>
                                        &nbsp;| Support: <strong>{formatClose(optionsData.niftySupport)}</strong>
                                        &nbsp;| Resistance: <strong>{formatClose(optionsData.niftyResistance)}</strong>
                                    </p>
                                    <p style={{ margin: '0.2rem 0' }}>
                                        BankNifty PCR: <strong>{optionsData.bankNiftyPCR}</strong>
                                        &nbsp;| Support: <strong>{formatClose(optionsData.bnSupport)}</strong>
                                        &nbsp;| Resistance: <strong>{formatClose(optionsData.bnResistance)}</strong>
                                    </p>
                                    {optionsData.narrative && (
                                        <p style={{ margin: '0.5rem 0 0', color: '#6b7280', fontSize: '0.82rem' }}>
                                            {optionsData.narrative}
                                        </p>
                                    )}
                                </div>
                            </Section>
                        )}

                        {/* IPO Tracker */}
                        {(ipoTracker?.recentListings?.length > 0 || ipoTracker?.upcoming?.length > 0) && (
                            <Section title="IPO Tracker" defaultOpen={false}>
                                {ipoTracker.recentListings?.length > 0 && (
                                    <>
                                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.35rem' }}>RECENT LISTINGS</p>
                                        {ipoTracker.recentListings.map((ipo, i) => (
                                            <div key={i} className="sector-row">
                                                <span>{ipo.name}{ipo.category && <span style={{ color: '#6b7280' }}> ({ipo.category})</span>}</span>
                                                <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{ipo.status}</span>
                                            </div>
                                        ))}
                                    </>
                                )}
                                {ipoTracker.upcoming?.length > 0 && (
                                    <>
                                        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.6rem 0 0.35rem' }}>UPCOMING</p>
                                        {ipoTracker.upcoming.map((ipo, i) => (
                                            <div key={i} className="sector-row">
                                                <span>{ipo.name}</span>
                                                <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{ipo.priceBand || ipo.status}</span>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </Section>
                        )}

                        {/* Results Calendar */}
                        {resultsCalendar?.rows?.length > 0 && (
                            <Section title="Results Calendar" defaultOpen={false}>
                                {resultsCalendar.rows.map((r, i) => (
                                    <div key={i} className="sector-row" style={{ alignItems: 'flex-start', flexDirection: 'column', gap: '0.15rem' }}>
                                        <span><strong>{r.company}</strong> <span style={{ color: '#6b7280' }}>({r.sector})</span> — {r.date}</span>
                                        <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{r.whatToWatch}</span>
                                    </div>
                                ))}
                            </Section>
                        )}

                {/* Looking Ahead */}
                {lookingAhead?.length > 0 && (
                    <Section title="Looking Ahead — Next Week" defaultOpen={false}>
                        <ul className="events-list">
                            {lookingAhead.map((item, i) => <li key={i}>{item}</li>)}
                        </ul>
                    </Section>
                )}

                {/* Our View — always shown */}
                {ourView && (
                    <div className="wrap-section" style={{ marginTop: '1rem' }}>
                        <h3 className="wrap-section-title">Our View</h3>
                        <div className="our-view-box">{ourView}</div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WeeklyWrapCard;
